import React, { createContext, useState, useEffect } from 'react';
import { createUser } from '../lib/models/user';
import { useRouting } from './shared/use-routing';
import { signIn } from '../lib/use-cases/sign-in';
import { signOut } from '../lib/use-cases/sign-out';
import { deleteAccount } from '../lib/use-cases/delete-account';
import { logEvent } from '../lib/shared/event-logger';
import { EVENT_TYPES } from '../lib/shared/event-types';
import { pagePathnames } from './pages/page-routes';
import { getUser } from '../lib/use-cases/get-user';
import { signInBySubscription } from '../lib/use-cases/sign-in-by-subscription';
import { endMockTestSession, isMockTestSession } from '../lib/shared/mock-test-user';
import { useLegalRegion } from './shared/use-legal-region';

const AppContext = createContext();

const AppProvider = ({ children }) => {
    const { navigate, params, buildPath } = useRouting();
    const [isLoading, setIsLoading] = useState(!Boolean(params.subscriptionId));
    const [userData, setUserData] = useState(createUser({}));
    const legalRegion = useLegalRegion();

    const init = async () => {
        try {
            await getUserData();
        } catch {
            // unauthenticated — expected, Sentry captures real errors automatically
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!params.subscriptionId) {
            init();
        } else {
            setIsLoading(false);
        }
    }, []);

    const getUserData = async () => {
        const newUserData = await getUser();

        setUserData(newUserData);
        logEvent(EVENT_TYPES.USER_ID, newUserData.id);

        return newUserData;
    };

    const updateUserData = async () => {
        const newUserData = await getUser();

        setUserData(newUserData);
    };

    const signInUser = async token => {
        try {
            await signIn(token);
            await getUserData();

            navigate(buildPath(pagePathnames.dashboard));
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const signInUserBySubscription = async subscriptionId => {
        await signInBySubscription(subscriptionId);
        await getUserData();
    };

    const signOutUser = async () => {
        window.zE?.('messenger', 'logoutUser');

        if (isMockTestSession()) {
            endMockTestSession();
        } else {
            signOut();
        }

        setUserData({});
        navigate(buildPath(pagePathnames.start));
    };

    const value = {
        // userData: { ...userData, legalRegion: userData.isAuthenticated ? userData.legalRegion : legalRegion },
        userData: { ...userData, legalRegion },
        isLoading,
        signIn: signInUser,
        signInBySubscription: signInUserBySubscription,
        signOut: signOutUser,
        getUserData,
        updateUserData,
        deleteAccount
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

export {
    AppProvider,
    AppContext
};
